import { Link } from "react-router-dom";
import { MapPin, CalendarClock, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { DemoBadge, RatingPill } from "@/components/common";
import { formatPrice, formatDate, formatTime, imageUrl } from "@/lib/format";

export const NextAvailable = ({ next }) => (
  <p className="flex items-center gap-1.5 text-xs font-medium text-teal-700">
    <CalendarClock className="h-3.5 w-3.5" />
    {next ? `Next: ${formatDate(next.date, "EEE d MMM")} · ${formatTime(next.time)}` : "No availability in next 2 weeks"}
  </p>
);

export default function ProviderCard({ provider: p, index = 0, showBook = true }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.35, delay: Math.min(index, 6) * 0.05 }}
      data-testid={`provider-card-${p.id}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm transition-shadow duration-200 hover:shadow-[0_12px_30px_-4px_rgba(2,132,199,0.14)]"
    >
      <Link to={`/provider/${p.id}`} className="relative block aspect-[16/10] overflow-hidden bg-slate-100">
        <img src={imageUrl(p.cover_image || p.profile_image)} alt={p.business_name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute left-3 top-3 flex gap-1.5">
          <span className="rounded-full bg-white/95 px-2.5 py-1 text-xs font-semibold text-slate-800 backdrop-blur">{p.category_name}</span>
          {p.is_demo && <DemoBadge />}
        </div>
      </Link>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start gap-3">
          <img src={imageUrl(p.profile_image)} alt="" className="-mt-10 h-14 w-14 rounded-xl border-4 border-white object-cover shadow-sm" />
          <div className="min-w-0 flex-1">
            <h3 className="truncate font-heading text-lg font-semibold text-slate-900" data-testid="provider-card-name">{p.business_name}</h3>
            <p className="mt-0.5 flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" />{p.suburb}, {p.city}</p>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <RatingPill rating={p.rating_avg} count={p.review_count} />
          <p className="text-sm text-slate-500">From <span className="font-heading text-base font-bold text-slate-900">{formatPrice(p.min_price)}</span></p>
        </div>
        <p className="mt-3 line-clamp-2 text-sm text-slate-600">{p.short_description}</p>
        <div className="mt-3"><NextAvailable next={p.next_available} /></div>
        <div className="mt-auto flex gap-2 pt-5">
          <Link to={`/provider/${p.id}`} data-testid={`view-profile-${p.id}`} className="flex h-11 flex-1 items-center justify-center rounded-xl border border-slate-300 text-sm font-semibold text-slate-700 transition-colors hover:border-sky-600 hover:text-sky-700">
            View Profile
          </Link>
          {showBook && (
            <Link to={`/book/${p.id}`} data-testid={`book-now-${p.id}`} className="flex h-11 flex-1 items-center justify-center gap-1 rounded-xl bg-sky-600 text-sm font-semibold text-white transition-colors hover:bg-sky-700">
              Book Now <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>
      </div>
    </motion.article>
  );
}
